import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LoaderCircle, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function AuthCompletePage({ refreshAuth }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("Finalizujemy dostęp do panelu…");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const complete = async () => {
      try {
        setPhase("Przygotowujemy Twoją sesję…");
        const auth = await refreshAuth();
        if (!mounted) return;

        if (!auth?.user) {
          setError("Nie udało się zakończyć logowania. Spróbuj ponownie za chwilę.");
          return;
        }

        setPhase("Przekierowujemy do właściwego miejsca…");
        window.setTimeout(() => {
          navigate(auth.user.landing_route || "/apply", { replace: true });
        }, 600);
      } catch (loadError) {
        if (!mounted) return;
        setError("Wystąpił problem podczas przygotowywania dostępu.");
      }
    };

    complete();
    return () => {
      mounted = false;
    };
  }, [navigate, refreshAuth]);

  return (
    <div className="min-h-screen bg-background px-4 py-10 text-foreground">
      <div className="mx-auto flex min-h-[75vh] max-w-2xl items-center justify-center">
        <Card className="w-full border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.35)]" data-testid="oauth-callback-status">
          <CardHeader className="pb-4">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/12 text-primary">
              {error ? <AlertTriangle className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
            </div>
            <CardTitle className="text-2xl font-semibold">Przygotowujemy Twój panel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {error ? (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive-foreground">
                <p className="font-medium">Nie udało się zakończyć procesu.</p>
                <p className="mt-2 text-sm text-red-100/90">{error}</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-primary/20 bg-primary/8 p-4 text-sm text-muted-foreground">
                <div className="mb-3 flex items-center gap-2 text-foreground">
                  <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
                  {phase}
                </div>
                <div className="space-y-3">
                  <div className="h-3 w-1/3 rounded-full bg-primary/35" />
                  <div className="h-3 w-full rounded-full bg-muted" />
                  <div className="h-3 w-4/5 rounded-full bg-muted" />
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-border bg-background/70 p-4 text-sm text-muted-foreground">
              Za chwilę przeniesiemy Cię do formularza lub do podglądu statusu zgłoszenia.
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="secondary" onClick={() => navigate("/", { replace: true })}>
                Wróć na stronę główną
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
